import React, { useCallback, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { v7 as uuidv7 } from "uuid";
import { usePageCounterClient } from "@contexts/PageCounterClient.context";
import { useClientProjectClient } from "@contexts/ClientProjectClient.context";
import PageCounterComponent from "@view/workspace/PageCounter";
import { buildPageCounterViewProps } from "@ps-bridge/pageCounter.program";
import * as Option from "@utils/Option";
import type { OptionTs, TaskEitherTs } from "@ps-bridge/interop";
import { ok, type Ok } from "@ps-bridge/ok";
import type {
  ClientListResponse,
  CreateClientRequest,
  CreateProjectRequest,
  PresignProjectDocumentsRequest,
  ProjectListResponse,
} from "@ps-bridge/models/core/dataContracts";
import { makeDocumentUploadFileName } from "@ps-bridge/models/brands/documentUploadFileName";
import { makeDocumentID } from "@ps-bridge/models/brands/documentID";
import { makeProjectID } from "@ps-bridge/models/brands/projectID";
import {
  ioEffect,
  taskEitherMap,
  taskEitherChain,
  taskEitherFromEffect,
  taskEitherFromIO,
  taskEitherLeft,
  taskEitherMapLeft,
  taskEitherOf,
  taskEitherTryCatch,
} from "@utils/effects/branded";
import { useRunTaskEither } from "@hooks/effects/useTaskEffect";
import type { PageCounterPhase, PageCounterPhasePayload } from "@view/panels/workspace/page-counter/PageCounterPhase";

type PageCounterProjectContext = {
  readonly clientId: string;
  readonly projectId: string;
};

type UploadTarget = {
  readonly context: PageCounterProjectContext;
  readonly presigned: {
    readonly document: {
      readonly id: string;
    };
    readonly upload_url: string;
  };
};

const PAGE_COUNTER_CLIENT_NAME = "Page Counter";
const PAGE_COUNTER_PROJECT_NAME = "Page Counter Uploads";

const isBackendLocalUploadUrl = (uploadUrl: string): boolean => {
  try {
    const parsed = new URL(uploadUrl, window.location.origin);
    return parsed.pathname.startsWith("/v1/clients/projects/") && parsed.pathname.endsWith("/upload");
  } catch {
    return false;
  }
};

const uploadRequestInitFor = (uploadUrl: string, file: File): RequestInit => {
  const base: RequestInit = {
    method: "PUT",
    body: file,
  };

  if (!isBackendLocalUploadUrl(uploadUrl)) {
    return base;
  }

  return {
    ...base,
    credentials: "include",
    headers: {
      "Content-Type": file.type || "application/octet-stream",
      "X-Trace-ID": uuidv7(),
    },
  };
};

const errorMessage = (reason: { readonly message: string } | string): string => {
  if (typeof reason === "string") {
    return reason;
  }
  const message = reason.message.trim();
  return message.length > 0 ? message : "Unexpected Page Counter error.";
};

const PageCounterPage: React.FC = () => {
  const { clientId, projectId } = useParams();
  const pageCounterClient = usePageCounterClient();
  const clientProjectClient = useClientProjectClient();
  const runTaskEither = useRunTaskEither();
  const [selectedFile, setSelectedFile] = useState<OptionTs<File>>(Option.none());
  const [phase, setPhase] = useState<PageCounterPhase>("idle");
  const [phasePayload, setPhasePayload] = useState<PageCounterPhasePayload[PageCounterPhase]>({
    selectedFileName: Option.none(),
  });
  const selectedFileName = useMemo<OptionTs<string>>(
    () => Option.map((file: File) => file.name)(selectedFile),
    [selectedFile],
  );
  const viewProps = useMemo(
    () =>
      buildPageCounterViewProps({
        kernel: {},
        pageCounterRouteLabel: "Page Counter",
        pdfGuidance: "Only PDFs can be page counted at this time.",
        uploadLabel: "Upload document",
        retryLabel: "Count pages",
        selectedFileName,
      }),
    [selectedFileName],
  );

  const resolvePageCounterContext = useCallback((): TaskEitherTs<string, PageCounterProjectContext> => {
    if (clientId && projectId) {
      return taskEitherOf({ clientId, projectId });
    }

    const listClientsTask = (): TaskEitherTs<string, ClientListResponse> =>
      taskEitherMapLeft(
        taskEitherFromEffect(clientProjectClient.listClients, (reason) => reason),
        errorMessage,
      );
    const listProjectsTask = (resolvedClientId: string): TaskEitherTs<string, ProjectListResponse> =>
      taskEitherMapLeft(
        taskEitherFromEffect(clientProjectClient.listProjects(resolvedClientId), (reason) => reason),
        errorMessage,
      );
    const createClientTask = (): TaskEitherTs<string, Ok> => {
      const request: CreateClientRequest = {
        name: PAGE_COUNTER_CLIENT_NAME as never,
      };
      return taskEitherMapLeft(
        taskEitherFromEffect(clientProjectClient.createClient(request), (reason) => reason),
        errorMessage,
      );
    };
    const createProjectTask = (resolvedClientId: string): TaskEitherTs<string, Ok> => {
      const request: CreateProjectRequest = {
        name: PAGE_COUNTER_PROJECT_NAME as never,
      };
      return taskEitherMapLeft(
        taskEitherFromEffect(clientProjectClient.createProject(resolvedClientId, request), (reason) => reason),
        errorMessage,
      );
    };
    const selectClient = (clients: ClientListResponse): TaskEitherTs<string, string> => {
      const firstClient = clients.items[0];
      return firstClient
        ? taskEitherOf(String(firstClient.id))
        : taskEitherLeft("Page Counter could not prepare a workspace client.");
    };
    const selectProject = (
      resolvedClientId: string,
      projects: ProjectListResponse,
    ): TaskEitherTs<string, PageCounterProjectContext> => {
      const firstProject = projects.items[0];
      return firstProject
        ? taskEitherOf({ clientId: resolvedClientId, projectId: String(firstProject.id) })
        : taskEitherLeft("Page Counter could not prepare a workspace project.");
    };
    const resolveProject = (resolvedClientId: string): TaskEitherTs<string, PageCounterProjectContext> =>
      taskEitherChain(listProjectsTask(resolvedClientId), (projects) => {
        const firstProject = projects.items[0];
        if (firstProject) {
          return taskEitherOf({ clientId: resolvedClientId, projectId: String(firstProject.id) });
        }

        return taskEitherChain(
          createProjectTask(resolvedClientId),
          () => taskEitherChain(listProjectsTask(resolvedClientId), (updatedProjects) =>
            selectProject(resolvedClientId, updatedProjects),
          ),
        );
      });

    return taskEitherChain(listClientsTask(), (clients) => {
      const firstClient = clients.items[0];
      if (firstClient) {
        return resolveProject(String(firstClient.id));
      }

      return taskEitherChain(
        createClientTask(),
        () => taskEitherChain(listClientsTask(), (updatedClients) =>
          taskEitherChain(selectClient(updatedClients), resolveProject),
        ),
      );
    });
  }, [clientId, clientProjectClient, projectId]);

  const uploadSelectedFile = useCallback((file: File) => {
    setPhase("processing");
    setPhasePayload({ selectedFileName: file.name, progressLabel: "Uploading document..." });

    const presignRequest: PresignProjectDocumentsRequest = {
      uploads: [
        {
          filename: makeDocumentUploadFileName(file.name),
          file_size: file.size as never,
          mime_type: (file.type || "application/octet-stream") as never,
          document_type: "uploaded" as never,
          suggested_classification: "unknown" as never,
          batch_id: undefined as never,
          source_document_ids: [] as never,
        },
      ],
    } as PresignProjectDocumentsRequest;
    const presignTask = taskEitherChain(resolvePageCounterContext(), (context) =>
      taskEitherMap(
        taskEitherMapLeft(
          taskEitherFromEffect(
            clientProjectClient.presignProjectDocuments(context.clientId, context.projectId, presignRequest),
            (reason) => reason,
          ),
          errorMessage,
        ),
        (response) => ({ context, response }),
      ),
    );
    const uploadTask = taskEitherChain(presignTask, ({ context, response }) => {
      const presigned = response.items[0];
      if (!presigned) {
        return taskEitherLeft("The upload service did not return an upload target.");
      }
      return taskEitherChain(
        taskEitherTryCatch(
          () => fetch(presigned.upload_url, uploadRequestInitFor(presigned.upload_url, file)),
          () => "The selected document could not be uploaded.",
        ),
        (uploadResponse) =>
          uploadResponse.ok
            ? taskEitherOf({ context, presigned } as UploadTarget)
            : taskEitherLeft("The selected document could not be uploaded."),
      );
    });
    const countTask = taskEitherChain(uploadTask, ({ context, presigned }) =>
      taskEitherChain(
        taskEitherFromIO(ioEffect(() => {
          setPhasePayload({ selectedFileName: file.name, progressLabel: "Counting pages..." });
          return ok;
        })),
        () => taskEitherMapLeft(
          pageCounterClient.countUploadedDocument({
            projectId: makeProjectID(context.projectId),
            documentId: makeDocumentID(String(presigned.document.id)),
          }),
          errorMessage,
        ),
      ),
    );

    runTaskEither(countTask, {
      onLeft: (message) => ioEffect(() => {
        setPhase("error");
        setPhasePayload({
          selectedFileName,
          message,
          retryLabel: "Retry",
        });
        return ok;
      }),
      onRight: (result) => ioEffect(() => {
        if (result.billingOutcome === "non_pdf") {
          setPhase("nonPdf");
          setPhasePayload({
            selectedFileName: result.filename,
            guidanceText: result.message,
            retryLabel: "Retry",
          });
          return ok;
        }

        setPhase("success");
        setPhasePayload({
          selectedFileName: result.filename,
          documentId: result.documentId,
          mimeType: result.mimeType,
          billablePages: result.billablePages,
          unitPriceCents: result.unitPriceCents,
          totalCostCents: result.totalCostCents,
          currency: result.currency,
          billingOutcome: result.billingOutcome,
          traceId: result.traceId,
          message: result.message,
        });
        return ok;
      }),
    });
    return ok;
  }, [clientProjectClient, pageCounterClient, resolvePageCounterContext, runTaskEither, selectedFileName]);

  const handleSubmit = useCallback(
    () => {
      return Option.fold(
        selectedFile,
        () => {
          setPhase("guidance");
          setPhasePayload({ guidanceText: "Only PDFs can be page counted at this time." });
          return ok;
        },
        (file) => {
          return uploadSelectedFile(file);
        },
      );
    },
    [selectedFile, uploadSelectedFile],
  );

  return (
      <PageCounterComponent
      {...viewProps}
      pageCounterPhase={phase}
      phasePayload={phasePayload}
      canSubmit={Option.fold(selectedFile, () => false, () => true) && phase !== "processing"}
      onFileChange={(value) => {
        setSelectedFile(value);
        const nextSelectedFileName = Option.map((file: File) => file.name)(value);
        setPhase("selecting");
        setPhasePayload({ selectedFileName: nextSelectedFileName });
        return ok;
      }}
      onSubmit={handleSubmit}
    />
  );
};

export default PageCounterPage;
